// NATIVE
import React, { useEffect, useState } from 'react';

// INSTALLED
import { useFormikContext } from 'formik';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import AutocompleteField from '../../formFields/autocompleteField';
import { FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';

// CUSTOM
import HourPicker from '../../formFields/HourPicker';
import InputNumeric from '../../formFields/InputNumeric';
import CheckboxField from '../../formFields/checkBoxField';
import { hideNotDaysWeekCreateShiftError } from '../../../store/shifts';

/**
 * CrearTurno component is responsible for rendering a form to create a new shift.
 * It utilizes various hooks and components to manage state, handle form submission,
 * and display form fields.
 *
 * @param {any} props - The properties passed to the component.
 * @returns {JSX.Element} The CrearTurno component.
 *
 * @component
 * @example
 * return (
 *   <CrearTurno formField={formField} />
 * )
 *
 * @remarks
 * This component uses:
 * - `useDispatch` from `react-redux` to dispatch actions.
 * - `useFormikContext` from `formik` to manage form state.
 * - `useSelector` from `react-redux` to access the Redux store state.
 * - `useState` and `useEffect` from `react` to manage local component state and side effects.
 *
 * The component renders:
 * - An AutocompleteField for selecting a cargo.
 * - HourPicker components for selecting start and end hours.
 * - RadioGroup for selecting the shift type (day, afternoon, night).
 * - InputNumeric components for entering the rate and year.
 * - CheckboxField components for selecting the days the shift applies to.
 *
 * The component also handles:
 * - Setting the initial value of the shift type to 'day'.
 * - Populating the cargos_select state with data from the cargos_list.
 * - Checking if at least one day is selected and displaying an error message if not.
 */
export const CrearTurno = (props: any) => {
  // USE DISPATCH
  const dispatch = useDispatch();

  // USE FORMIK
  const formikProps: any = useFormikContext();

  // USE SELECTOR
  const { days }: any = useSelector((state: any) => state.turnos);
  const { cargos_list }: any = useSelector((state: any) => state.cargos);

  // USE STATE
  const [value, setValue] = React.useState('dia');
  const [cargos_select, setCargosSelect] = useState([]);

  // FORM FIELDS
  const {
    formField: {
      cargo,
      start_hour,
      end_hour,
      dia,
      tarde,
      noche,
      tarifa,
      anio,
      lunes,
      martes,
      miercoles,
      jueves,
      viernes,
      sabado,
      domingo,
      festivo,
    },
  } = props;

  // USE EFFECT
  useEffect(() => {
    const areas: any = [];

    if (cargos_list.length > 0) {
      for (let index = 0; index < cargos_list.length; index++) {
        const data = {
          label: cargos_list[index].name,
          value: cargos_list[index].id,
        };
        areas.push(data);
      }
      setCargosSelect(areas);
    }
  }, [cargos_list]);

  useEffect(() => {
    formikProps.setFieldValue('tipoTurno', 'dia');
  }, []);

  // CONST STYLE
  const Div = styled('div')(({ theme }) => ({
    ...theme.typography.button,
    backgroundColor: 'wheat',
    padding: theme.spacing(8),
    textAlign: 'center',
  }));

  // CONST FUNCTIONS
  const handleChange = (event: any) => {
    formikProps.setFieldValue('tipoTurno', event.target.value);
    setValue(event.target.value);
  };

  const checkDay = async (event: any) => {
    if (event.target.checked) {
      if (days) {
        await dispatch(hideNotDaysWeekCreateShiftError());
      }
    }
  };

  return (
    <div>
      <Grid container>
        <Grid size={{ xs: 12 }} sx={{ padding: 2 }}>
          {cargos_select.length > 0 ? (
            <Grid size={{ xs: 12 }}>
              <AutocompleteField
                options={cargos_select}
                name={cargo.name}
                label={cargo.label}
                element={cargo.name}
              />
            </Grid>
          ) : (
            <Div>{'No hay areas disponibles...'}</Div>
          )}
        </Grid>
        <Grid size={{ xs: 6 }} sx={{ padding: 2 }}>
          <HourPicker label={start_hour.label} name={start_hour.name} />
        </Grid>
        <Grid size={{ xs: 6 }} sx={{ padding: 2 }}>
          <HourPicker label={end_hour.label} name={end_hour.name} />
        </Grid>

        <Grid container spacing={2} sx={{ padding: 2, justifyContent: 'center' }}>
          <RadioGroup
            value={value}
            onChange={handleChange}
            style={{ display: 'flex', flexDirection: 'row' }}
          >
            <Grid size={{ xs: 4 }} sx={{ padding: 2 }}>
              <FormControlLabel value={dia.name} control={<Radio />} label={dia.label} />
            </Grid>
            <Grid size={{ xs: 4 }} sx={{ padding: 2 }}>
              <FormControlLabel value={tarde.name} control={<Radio />} label={tarde.label} />
            </Grid>
            <Grid size={{ xs: 4 }} sx={{ padding: 2 }}>
              <FormControlLabel value={noche.name} control={<Radio />} label={noche.label} />
            </Grid>
          </RadioGroup>
        </Grid>
        <Grid size={{ xs: 7 }} sx={{ padding: 2 }}>
          <InputNumeric name={tarifa.name} label={tarifa.label} variant="outlined" fullWidth />
        </Grid>
        <Grid size={{ xs: 5 }} sx={{ padding: 2 }}>
          <InputNumeric name={anio.name} label={anio.label} variant="outlined" fullWidth />
        </Grid>

        {!days && (
          <Grid container sx={{ justifyContent: 'center', paddingTop: 2 }}>
            <Typography variant="h6" component="h6">
              Días que aplica el turno
            </Typography>
          </Grid>
        )}

        {days && (
          <Grid container sx={{ justifyContent: 'center', paddingTop: 2 }}>
            <Typography variant="h6" component="h6" color={'red'}>
              Debes selecionar al menos un dia *
            </Typography>
          </Grid>
        )}

        <Grid container spacing={2} sx={{ padding: 2 }}>
          <Grid size={{ xs: 3 }}>
            <CheckboxField name={lunes.name} label={lunes.label} onChange={checkDay} />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <CheckboxField name={martes.name} label={martes.label} />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <CheckboxField name={miercoles.name} label={miercoles.label} />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <CheckboxField name={jueves.name} label={jueves.label} />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ padding: 2 }}>
          <Grid size={{ xs: 3 }}>
            <CheckboxField name={viernes.name} label={viernes.label} />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <CheckboxField name={sabado.name} label={sabado.label} />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <CheckboxField name={domingo.name} label={domingo.label} />
          </Grid>
          <Grid size={{ xs: 3 }}>
            <CheckboxField name={festivo.name} label={festivo.label} />
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};
